/**
 * FACTORY-V6-ACTIVATION-V1 -- the ONE switch that selects the reference
 * visual system (the founder calls it V6; the attribute it sets is
 * `data-visual="v1"`, the name #706 shipped and every CSS rule in
 * dashboard.css already keys on), the ONE place that says WHERE it may
 * render, and the ONE place that says HOW LONG a walker's choice lasts.
 *
 * WHAT CHANGED FROM ADORACARE-VISUAL-SYSTEM-V1 (#706), and why.
 * #706 fenced the switch to ONE screen and wrote so in its own header:
 * "Conforming another screen is a ROW in VISUAL_V1_ROUTES, never new logic
 * -- and it waits on the founder's approval of this reference." That
 * approval arrived as the founder's four-lane order of 2026-09-10, which
 * makes V6 the standard for the delivery window and gives the Factory lane
 * one job here: "Ship the shared activation once". So this module now
 * carries ROWS (the demo-route allowlist below, each one CITED) and exactly
 * the logic that order named -- hold across navigation, hold across
 * refresh, hold on the print routes, clean up outside the allowlist.
 *
 * The front-desk Home, billing and doctor consult use V6 as their normal presentation by the founder's 2026-09-11
 * instruction: match the approved design without a preview flag. The shared
 * resolver applies it on those exact routes, with no organisation setting or
 * remembered preview choice required. Later approved normal routes include
 * the manager workspace and exact Reports route (cycle21); their role guards
 * remain unchanged. Other routes remain as follows.
 *
 * PREVIEW DEFAULT OFF. Other screens render V6 only when a walker
 * asks for it with `?visual=v1` (or an org is enabled for it -- see
 * `orgEnabled` below, which no caller passes yet). The difference is that
 * the ask is no longer thrown away by the next click: it is remembered for
 * the TAB (sessionStorage), because a demo is a WALK across screens and a
 * switch that dies on the first nav link cannot be walked. `?visual=current`
 * is the explicit off-switch and is remembered the same way.
 *
 * WHY A TAB, NOT A COOKIE OR A SERVER FLAG. Tab-scoped memory keeps the
 * founder's side-by-side intact -- two tabs, one switched, one not, on the
 * same build -- and it expires when the tab closes, so no walker is left
 * stranded in a preview they cannot get out of.
 *
 * NOT AN ENV FLAG ON PURPOSE (feature-flag doctrine, COLLABORATION-STANDARD
 * s.26; tests/unit/feature-flag-allowlist-guard.test.ts): the doctrine
 * reserves env flags for outbound / PHI / schema risk. A visual system is
 * presentation. It grants NOTHING: authority stays with the route guards,
 * the server fences every wire either way, and no field becomes visible
 * because a walker typed a query string.
 *
 * Pure module (no React, no I/O, no `document`, no `window`) so the root and
 * frontend suites pin it. The DOM and the storage both arrive as arguments.
 * The React seam is `useVisualActivation.ts`, mounted ONCE in the shell.
 */

export type VisualVariant = 'current' | 'v1';

/** The query key and the two values that select a variant. */
export const VISUAL_VARIANT_QUERY_KEY = 'visual';
export const VISUAL_V1_VALUE = 'v1';
/** The explicit OFF switch -- how a walker leaves the preview mid-walk. */
export const VISUAL_CURRENT_VALUE = 'current';

/** The attribute the CSS keys on. Set on `<html>`, never on a component. */
export const VISUAL_ROOT_ATTRIBUTE = 'data-visual';

/** The tab-scoped memory key. One key, namespaced, never reused. */
export const VISUAL_MEMORY_KEY = 'adoracare.visual';

/**
 * A remembered walker choice: 'v1' asked for it, 'current' explicitly left
 * it, null never said either way. NOT the variant -- the variant is this
 * intent AFTER the route fence is applied.
 */
export type VisualMemory = VisualVariant | null;

/**
 * ONE demo route. `pattern` is matched by `matchesVisualRoute` below;
 * `leg` says which demo journey the row serves and `source` cites the repo
 * line the route came from, so no row is here on somebody's memory.
 * `reviewedUnderV6` records whether the founder has SEEN this screen with
 * the switch on -- false rows are the ones the frames exist to show.
 */
export interface VisualV1Route {
  readonly pattern: string;
  readonly leg: string;
  readonly source: string;
  readonly reviewedUnderV6: boolean;
}

/**
 * THE DEMO-ROUTE ALLOWLIST.
 *
 * Six of these eight rows are the SIX FOUNDER-RATIFIED ROLE HOMES
 * (2026-09-04), taken verbatim -- route and citation both -- from the
 * instrument that measured them:
 * tests/e2e/role-acceptance/global-role-conformance-v1-captures.spec.ts.
 * That run reported `data-visual: (absent) -> (absent)` and a 0.0000% pixel
 * delta on all six, because the #706 fence made the switch INERT there. The
 * screens were therefore never seen under V6 -- which is exactly what these
 * rows change, and why every one of them carries reviewedUnderV6: false.
 *
 * THE ALLOWLIST RULE (controller ruling on PR #773, 2026-09-10). These eight
 * rows are an OPT-IN PREVIEW SURFACE and nothing more -- `?visual=v1`,
 * tab-sticky, default OFF. Four standing rules govern the table:
 *
 *   (a) `reviewedUnderV6` IS THE HONEST LEDGER, and no lane flips a row to
 *       true from its own read. It stays FALSE on /dashboard/doctor/home and
 *       on /dashboard/coc/patient-followups until Lane 1 / Lane 4 land their
 *       V6 fixes AND the founder rules SHIP on frames. Pinned by name in
 *       __tests__/visual-variant.test.ts.
 *   (b) `orgEnabled` STAYS UNWIRED. The resolution accepts it and is pinned,
 *       but no product code passes it -- also pinned, by reading this
 *       package's own sources. Org-wide activation waits on review of the
 *       journeys' routes; the gate is REVIEW, not a date.
 *   (c) PRINT ROUTES STAY OUT -- /dashboard/patients/consent-print and
 *       /dashboard/patients/treatment-consent-print are chrome-free sheets
 *       that print on the browser's default stylesheet, and V6 changes the
 *       body font. Pinned as absent.
 *   (d) A NEW ROW ARRIVES AS A HANDOFF, never as a lane's own edit: the
 *       product lane sends FOR FACTORY the route pattern, its App.tsx source
 *       line, and a gallery path + sha; the factory lane lands it in its own
 *       PR with 1440 + 834 OFF/ON frames from the live DOM. No row without a
 *       frame; no row marked reviewed without the founder's SHIP.
 *
 * A ROW IS NEVER ADDED WITHOUT A FRAME BEHIND IT. The evidence gallery for
 * this package captures every row at 1440 and 834, switch OFF and switch ON,
 * from the live DOM with NO INJECTED ATTRIBUTE.
 */
export const VISUAL_V1_DEMO_ROUTES: readonly VisualV1Route[] = [
  {
    pattern: '/dashboard/callcenter/daily-report',
    leg: 'Call centre -- Reports (normal route)',
    source: 'Lane2 founder-approved cycle21 normal Reports; frontend/src/App.tsx callcenter/daily-report; lane2-manager-vertical-evidence/index.html',
    reviewedUnderV6: false,
  },
  {
    pattern: '/dashboard/callcenter/workspace',
    leg: 'Call centre -- My Work (the conformed reference)',
    source: 'ADORACARE-VISUAL-SYSTEM-V1 #706; frontend/src/App.tsx path="callcenter/workspace"',
    reviewedUnderV6: true,
  },
  {
    pattern: '/dashboard/lead-cockpit',
    leg: 'Call centre -- Command center (role home)',
    source:
      'lib/dashboard-role-view.ts (call_center_agent homePath) + ' +
      'frontend/src/App.tsx path="lead-cockpit" + nav-config.ts (Command center)',
    reviewedUnderV6: false,
  },
  {
    pattern: '/walkin/*',
    leg: 'Front desk -- Today, and the walk-in registration legs (role home)',
    source:
      'lib/dashboard-role-view.ts (receptionist homePath) + ' +
      'frontend/src/App.tsx path="/walkin/*" + nav-config.ts (Home)',
    reviewedUnderV6: false,
  },
  {
    pattern: '/dashboard/doctor/home',
    leg: "Doctor -- My day (role home)",
    source:
      'lib/dashboard-role-view.ts (doctor homePath) -> frontend/src/App.tsx ' +
      'redirect -> path="doctor/home" + nav-config.ts (Home)',
    reviewedUnderV6: false,
  },
  {
    pattern: '/dashboard/coc',
    leg: 'COC -- My clinics (role home)',
    source:
      'lib/dashboard-role-view.ts (coc homePath) + ' +
      'frontend/src/App.tsx path="coc" + nav-config.ts (My clinics)',
    reviewedUnderV6: false,
  },
  {
    pattern: '/dashboard/coc/coverage',
    leg: 'COC -- Doctor coverage workspace',
    source: 'frontend/src/App.tsx path="coc/coverage"; #836 composition at 3aab1d7',
    reviewedUnderV6: false,
  },
  {
    pattern: '/dashboard/coc/patient-followups',
    leg: 'COC -- the follow-up register (the COC leg of the tester walk)',
    source:
      'COC-FOLLOWUP-REGISTER-V2 #765 + frontend/src/App.tsx ' +
      'path="coc/patient-followups"',
    reviewedUnderV6: false,
  },
  {
    pattern: '/dashboard/home',
    leg: 'Owner -- Home (role home)',
    source:
      'lib/dashboard-role-view.ts (owner homePath) + ' +
      'frontend/src/App.tsx path="home" + nav-config.ts (Home)',
    reviewedUnderV6: false,
  },
  {
    pattern: '/dashboard/agm',
    leg: 'AGM/GM -- Zone overview (one shared console; role home)',
    source:
      'lib/dashboard-role-view.ts (gm_agm AND regional_head share this ' +
      'homePath) + frontend/src/App.tsx path="agm" + nav-config.ts (Zone overview)',
    reviewedUnderV6: false,
  },
];

/**
 * The patterns alone. Kept as the module's original export name and shape
 * (`readonly string[]`) so every existing importer and pin reads unchanged.
 */
export const VISUAL_V1_ROUTES: readonly string[] = VISUAL_V1_DEMO_ROUTES.map(
  (route) => route.pattern,
);

function normalizePathname(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

/**
 * Match ONE pattern against ONE pathname. Two forms, both deliberately
 * small -- this module stays React-free, so react-router's `matchPath` is
 * not available and is not wanted:
 *
 *   `/a/b`    exact. `/a/b` matches; `/a/b/c` does NOT (which is what keeps
 *             the `/dashboard/coc` home row off `/dashboard/coc/billing`).
 *   `/a/*`    prefix. `/a` and `/a/anything/deep` both match; `/ab` does not.
 *
 * There is no `:param` form and no row needs one today. If a row ever does,
 * add the form here with its own test -- never a regex in a caller.
 */
export function matchesVisualRoute(pattern: string, pathname: string): boolean {
  const path = normalizePathname(pathname);
  if (pattern.endsWith('/*')) {
    const prefix = normalizePathname(pattern.slice(0, -2));
    return path === prefix || path.startsWith(`${prefix}/`);
  }
  return path === normalizePathname(pattern);
}

/** Founder-authorized product presentation: billing, consult, call-centre and COC coverage.
 * No preview flag; sibling pages and print routes are not included. */
function isNormalV6ProductRoute(pathname: string): boolean {
  return matchesVisualRoute('/dashboard/coc/coverage', pathname) ||
    matchesVisualRoute('/walkin', pathname) ||
    matchesVisualRoute('/dashboard/billing-entry', pathname) ||
    matchesVisualRoute('/dashboard/money', pathname) ||
    /^\/dashboard\/patients\/(?!new(?:\/|$)|merge-queue(?:\/|$)|consent-print(?:\/|$)|treatment-consent-print(?:\/|$))[^/]+\/?$/.test(pathname) ||
    matchesVisualRoute('/dashboard/callcenter/workspace', pathname) ||
    matchesVisualRoute('/dashboard/callcenter/daily-report', pathname) ||
    /^\/dashboard\/doctor\/consult\/[^/]+\/?$/.test(pathname);
}

/** TRUE when the pathname is a route the reference system is allowed on. */
export function isVisualV1Route(pathname: string): boolean {
  return isNormalV6ProductRoute(pathname) || VISUAL_V1_ROUTES.some((pattern) => matchesVisualRoute(pattern, pathname));
}

/**
 * The variant for a location, from the URL ALONE and with no memory. Kept
 * because the workspace page reads it directly to pick its own render, and
 * because the #706 pins are written against it.
 */
export function resolveVisualVariant(location: {
  readonly pathname: string;
  readonly search: string;
}): VisualVariant {
  if (isNormalV6ProductRoute(location.pathname)) return 'v1';
  if (!isVisualV1Route(location.pathname)) return 'current';
  const params = new URLSearchParams(location.search);
  return params.get(VISUAL_VARIANT_QUERY_KEY) === VISUAL_V1_VALUE ? 'v1' : 'current';
}

/** The walker's intent carried by ONE URL, or null when it carries none. */
export function readVisualIntent(search: string): VisualMemory {
  const value = new URLSearchParams(search).get(VISUAL_VARIANT_QUERY_KEY);
  if (value === VISUAL_V1_VALUE) return 'v1';
  if (value === VISUAL_CURRENT_VALUE) return 'current';
  return null;
}

export interface VisualActivationInput {
  readonly pathname: string;
  readonly search: string;
  /** What the tab already remembers. null = the walker never said. */
  readonly remembered: VisualMemory;
  /**
   * The per-org default, for the day an org is enabled for V6 without any
   * walker typing a query. NO CALLER PASSES THIS YET: the signal it needs is
   * a /me fragment on an API handler this lane does not own, so it ships as
   * a written proposal beside this package, not as an edit. The resolution
   * is built and pinned here so wiring it is one line in the shell.
   */
  readonly orgEnabled?: boolean;
}

export interface VisualActivation {
  /** What to render NOW, after the route fence. */
  readonly variant: VisualVariant;
  /** What the tab should remember AFTER this navigation. */
  readonly memory: VisualMemory;
}

/**
 * THE ONE RESOLUTION. Two decisions, kept apart on purpose:
 *
 *   INTENT is route-blind and sticky -- the URL's `visual=` if it carries
 *   one, else what the tab remembers, else the org default. This is what
 *   makes the switch HOLD across navigation and across a refresh (the tab
 *   remembers; the URL need not).
 *
 *   VARIANT is the intent AFTER the allowlist -- so a demo route renders V6
 *   while the walker's intent stands, and every route OUTSIDE the allowlist
 *   renders `current` WITHOUT forgetting the intent. That is the cleanup the
 *   order asked for: stepping off the demo path turns the system off on
 *   screen; stepping back on turns it back on, with nothing retyped.
 *
 * An org default is never written to memory: it is not the walker's choice,
 * and remembering it would outlive the org being switched off.
 */
export function resolveVisualActivation(input: VisualActivationInput): VisualActivation {
  const urlIntent = readVisualIntent(input.search);
  const memory: VisualMemory = urlIntent ?? input.remembered;
  const intent: VisualMemory = memory ?? (input.orgEnabled ? 'v1' : null);
  const variant: VisualVariant =
    isNormalV6ProductRoute(input.pathname) || (intent === 'v1' && isVisualV1Route(input.pathname)) ? 'v1' : 'current';
  return { variant, memory };
}

/** The minimum of `HTMLElement` this module writes. Keeps it DOM-free. */
export interface VisualRootElement {
  setAttribute(name: string, value: string): void;
  removeAttribute(name: string): void;
}

/**
 * Write the variant onto the document root -- the ONE place `data-visual`
 * is ever set or removed. `current` REMOVES the attribute rather than
 * setting it to a value, so a screen with the system off is byte-identical
 * to a build that never had the system (the #706 posture, unchanged).
 */
export function applyVisualActivation(
  root: VisualRootElement,
  variant: VisualVariant,
): void {
  if (variant === 'v1') {
    root.setAttribute(VISUAL_ROOT_ATTRIBUTE, VISUAL_V1_VALUE);
    return;
  }
  root.removeAttribute(VISUAL_ROOT_ATTRIBUTE);
}

/** The minimum of `Storage` this module reads and writes. */
export interface VisualMemoryStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Read the tab's memory. Anything that is not one of the two known values
 * -- a stale key, a hand-edited value, a null store -- reads as "never
 * said", so a junk value can never strand a walker in a preview.
 */
export function readVisualMemory(store: VisualMemoryStore | null): VisualMemory {
  if (!store) return null;
  try {
    const value = store.getItem(VISUAL_MEMORY_KEY);
    if (value === VISUAL_V1_VALUE) return 'v1';
    if (value === VISUAL_CURRENT_VALUE) return 'current';
    return null;
  } catch {
    // A store that throws on read (private mode, blocked storage) is a
    // store that never said. Never a crash on a demo screen.
    return null;
  }
}

/** Write the tab's memory; null clears the key. Never throws. */
export function writeVisualMemory(
  store: VisualMemoryStore | null,
  memory: VisualMemory,
): void {
  if (!store) return;
  try {
    if (memory === null) {
      store.removeItem(VISUAL_MEMORY_KEY);
      return;
    }
    store.setItem(VISUAL_MEMORY_KEY, memory);
  } catch {
    // Same posture as the read: the preview degrades to URL-only, the
    // screen keeps rendering.
  }
}

/**
 * Carry the switch across a URL rewrite. A screen whose filter state IS the
 * URL (the lead-list-url precedent) serialises fresh params on every filter
 * click, which would silently drop the switch and flip the founder's walk
 * back to the current render mid-comparison. Copies `visual=v1` from
 * `from` into `into` when -- and only when -- `from` carries it; returns
 * `into` for chaining. Never adds the switch on its own.
 *
 * Tab memory has NOT made this redundant: the memory decides what RENDERS,
 * this decides what the founder's address bar still SAYS after a filter
 * click, and a side-by-side is walked from two URLs.
 */
export function carryVisualVariant(
  from: URLSearchParams,
  into: URLSearchParams,
): URLSearchParams {
  if (from.get(VISUAL_VARIANT_QUERY_KEY) === VISUAL_V1_VALUE) {
    into.set(VISUAL_VARIANT_QUERY_KEY, VISUAL_V1_VALUE);
  }
  return into;
}
